'use client';

// ---------------------------------------------------------------------------
// LocaleLink.tsx — Locale-aware Link for public pages
// ---------------------------------------------------------------------------
// Wraps next/link to auto-prefix hrefs with the current locale when the user
// is browsing a non-English locale. English (default) uses unprefixed URLs.
//
// Usage:
//   <LocaleLink href="/features">Features</LocaleLink>
//   → renders /features (en) or /ar/features (ar)
// ---------------------------------------------------------------------------

import Link from 'next/link';
import type { ComponentProps } from 'react';
import { useLocaleStore } from './locale.store';
import { DEFAULT_LOCALE } from './i18n.config';

type LinkProps = ComponentProps<typeof Link>;

/**
 * Hook that returns a locale-prefixed href.
 * - English (default locale): returns href as-is
 * - Other locales: prepends /{locale} to the href
 * - External URLs and anchors are passed through unchanged
 */
/** Paths that should NOT get locale prefix (non-public routes) */
const SKIP_PREFIXES = ['/api', '/auth', '/dashboard', '/pms', '/chain', '/platform', '/settings'];

export function useLocaleHref(href: string): string {
  const locale = useLocaleStore((s) => s.locale);
  if (locale === DEFAULT_LOCALE) return href;
  // Skip external URLs, anchors, and non-public routes
  if (href.startsWith('http') || href.startsWith('#')) return href;
  if (SKIP_PREFIXES.some((p) => href.startsWith(p))) return href;
  // Skip if already prefixed with locale
  if (href.startsWith(`/${locale}/`) || href === `/${locale}`) return href;
  // Prefix with locale
  return href === '/' ? `/${locale}` : `/${locale}${href}`;
}

/**
 * Locale-aware Link component for public pages.
 * Auto-prefixes the href with the current locale (unless English).
 */
export function LocaleLink({ href, ...props }: LinkProps) {
  const hrefStr = typeof href === 'string' ? href : href.pathname ?? '/';
  const localizedHref = useLocaleHref(hrefStr);
  return <Link {...props} href={localizedHref} />;
}
